import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Chip from '$tablet-app/src/app/components/Chip';
import { FloatingActions } from '$tablet-app/src/app/components/FloatingActions';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { OptionModel } from '$tablet-app/src/app/options/types';
import { Recipe } from '$tablet-app/src/app/recipes/types';
import { Button } from '@akin-home/buttons/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC } from 'react';
import {
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import styled from 'styled-components/native';
import RecipeSelectTile from '../RecipeSelectTile';

interface Props {
  /** The screen title */
  title?: string;

  favourite_recipes?: Recipe[];

  /** The recipes to be displayed in the view */
  recipes?: Recipe[];

  /** Status */
  status?: 'loading' | 'error' | 'success' | 'idle';

  /** The text in the filter */
  filterText?: string;

  /** Selected filter options */
  selectedFilters?: OptionModel[];

  /** Selected recipe */
  selectedRecipe?: Recipe;

  favourites?: string[];

  isLoading?: boolean;

  onSelectRecipe?: (recipe: Recipe) => void;

  onCancelSelectedRecipe?(): void;

  /** Callback for when a recipe is clicked */
  onClickRecipe?: (recipe: Recipe) => void;

  onChangeFilterText?: (text: string) => void;

  /** Callback for selection is confirmed */
  onConfirm?(): void;

  /** Callback for selection is cancelled */
  onCancel?: () => void;

  onPressShowFilters?: () => void;

  onPressRemoveFilter?: (id: string) => void;

  onPressFavourite?: (recipeID: string) => void;

  onScrollToBottom?: () => void;
}

/** UI for Recipe Selection/Swapping */
const RecipeSelectScreen: FC<Props> = (props) => {
  const {
    title = 'Select a recipe',
    isLoading,
    recipes,
    favourite_recipes,
    filterText,
    selectedFilters,
    selectedRecipe,

    onSelectRecipe,
    onClickRecipe,
    onChangeFilterText,
    onConfirm,
    onCancel,
    onPressShowFilters,
    onPressRemoveFilter,
    onScrollToBottom,
    onCancelSelectedRecipe,
  } = props;

  const { width } = useWindowDimensions();
  const isPortrait = width < 600;

  return (
    <Container>
      <Header>
        <ScreenBackButton onPress={onCancel} />
        <Text variant="h2" weight="semibold">
          {title}
        </Text>
      </Header>

      <ScrollView onScrollEndDrag={onScrollToBottom}>
        <BannerImage source={require('../../assets/select_recipe_banner.png')}>
          <PaddedBox>
            <InputsContainer>
              <SearchInputContainer>
                <SearchIcon icon="search" size={24} />

                <SearchInput
                  placeholder="Search recipes"
                  value={filterText}
                  onChangeText={onChangeFilterText}
                />
                <View>
                  <FilterButton
                    variant="text"
                    size="large"
                    onPress={onPressShowFilters}
                    icon={['far', 'sliders-h']}
                  >
                    {isPortrait ? null : <Text>Filter</Text>}
                  </FilterButton>
                </View>
              </SearchInputContainer>
              <RecipeFilters horizontal>
                <FiltersRow>
                  {selectedFilters?.map((filter) => (
                    <FilterContainer
                      key={filter.id}
                      onPress={() => onPressRemoveFilter?.(filter.id)}
                      testID={`recipe-filter-${filter.id}`}
                    >
                      <Chip iconRight="times" color="white">
                        {filter.excl ? (
                          <FontAwesomeIcon icon="ban" color="red" />
                        ) : (
                          <FontAwesomeIcon
                            icon="check-circle"
                            color="#535AF1"
                          />
                        )}
                        <Spacer width="8px" />
                        <Text variant="p2" weight="semibold">
                          {filter.label}
                        </Text>
                      </Chip>
                    </FilterContainer>
                  ))}
                </FiltersRow>
              </RecipeFilters>
            </InputsContainer>
          </PaddedBox>
        </BannerImage>

        {isLoading ? (
          <FullScreenLoader />
        ) : recipes?.length ? (
          <Body>
            {favourite_recipes?.length ? (
              <>
                <Text variant="h2">My Favourites</Text>
                <RecipeContainer>
                  {favourite_recipes?.map((recipe) => (
                    <RecipeSelectTile
                      key={recipe.id}
                      recipe={recipe}
                      onSelectRecipe={onSelectRecipe}
                      onClickRecipe={onClickRecipe}
                      isChosen={selectedRecipe?.id === recipe.id}
                    />
                  ))}
                </RecipeContainer>
              </>
            ) : null}

            <Text variant="h2">Recipes</Text>
            <RecipeContainer>
              {recipes.map((recipe) => (
                <RecipeSelectTile
                  key={recipe.id}
                  recipe={recipe}
                  onSelectRecipe={onSelectRecipe}
                  onClickRecipe={onClickRecipe}
                  isChosen={selectedRecipe?.id === recipe.id}
                />
              ))}
            </RecipeContainer>
          </Body>
        ) : (
          <EmptySearchText variant="h3">
            There are no recipes that match your search
          </EmptySearchText>
        )}
      </ScrollView>

      {onConfirm ? (
        <Actions>
          <Button
            size="xlarge"
            variant="white"
            onPress={onCancelSelectedRecipe}
          >
            Cancel
          </Button>
          <Button size="xlarge" onPress={onConfirm}>
            Confirm
          </Button>
        </Actions>
      ) : null}
    </Container>
  );
};

const Container = styled.View`
  display: flex;
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
  padding: 20px;
`;

const BannerImage = styled.ImageBackground`
  height: 240px;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;

const PaddedBox = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
  width: 100%;
  max-height: 100%;
`;

const InputsContainer = styled.View`
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
  max-width: 800px;
  flex: 1;
`;

const SearchInputContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: 22px;
  padding: 5px 20px;
  height: 50px;
  width: 100%;
  gap: 10px;
  background-color: white;
`;

const SearchIcon = styled(FontAwesomeIcon)`
  align-self: center;
`;

const SearchInput = styled.TextInput`
  font-size: 24px;
  flex: 1;
  min-width: 0;
`;

const FilterButton = styled(NewButton)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  padding: 0 10px;
  margin: 0;
  flex-grow: 1;
  align-self: center;
`;

const FiltersRow = styled.View`
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
`;

const Body = styled.View`
  padding: 20px;
  gap: 20px;
`;

const RecipeContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
`;

const EmptySearchText = styled(Text)`
  align-self: center;
  text-align: center;
  margin-top: 10%;
  margin-bottom: 10%;
`;

const Actions = styled(FloatingActions)`
  background-color: white;
  bottom: 0;
  padding: 20px;
  border-top-color: lightgrey;
  border-top-width: 2px;
  elevation: 2;
  justify-content: space-around;
  gap: 0;
`;

const RecipeFilters = styled(ScrollView)`
  width: 100%;
  flex-grow: 0;
  min-height: 0px;
  padding: 0px;
`;

const FilterContainer = styled(TouchableOpacity)`
  margin: 0px;
  padding: 0px;
  flex-shrink: 1;
`;

export default RecipeSelectScreen;
